import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search, Upload, Plus, Edit2, Trash2, Save,
    FileSpreadsheet, X, Filter, BookOpen, GraduationCap,
    Eye, GripVertical, ClipboardCheck, Users, Clock,
    Target, CheckCircle, AlertTriangle, Download
} from "lucide-react";

import {
    fetchFormationsRequest,
    createFormationRequest,
    updateFormationRequest,
    deleteFormationRequest,
    downloadFormationsTemplateRequest,
    importFormationsExcelRequest,
    exportFormationsRequest,
    clearFormationsImportResult,
} from "../../../../redux/actions/formationsActions";

import {
    fetchDomainesRequest,
    fetchThemesRequest,
    fetchThemesByDomaineRequest,
} from "../../../../redux/actions/paramDomainesThemesActions";

import { API_BASE_URL, putRequest } from "../../../../../api";
import "../RessourcesStyles/FormationManagement.css";

const emptyForm = {
    reference: "",
    nom: "",
    description: "",
    duration: "",
    cout: "",
    nombreMaxGroupe: "",
    ordre: "",
    prerequis: "",
    objectif: "",
    objectif2: "",
    objectif3: "",
    typeDuree: "Jours",
    moyenPedagogiques: "",
    publiqueVise: "",
    type: "Présentielle",
    periods: "",
    niveaux: "",
    domaineId: "",
    themeId: "",
    imageFile: null,
};

/* ─────────────────────────────────────────────
   Modal CRUD (standard)
───────────────────────────────────────────── */
const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;
    return (
        <div className="fm-modal-overlay" onMouseDown={onClose}>
            <div className="fm-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="fm-modal-header">
                    <h3>
                        <GraduationCap size={16} style={{ color: "#dc3545", marginRight: 8 }} />
                        {title}
                    </h3>
                    <button className="fm-x" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="fm-modal-body">{children}</div>
                {footer && <div className="fm-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   Modal Détails — inspiré DemanderFormation (INCHANGÉ)
───────────────────────────────────────────── */
const FormationDetailsModal = ({ open, onClose, formation, onEdit }) => {
    if (!open || !formation) return null;
    const imageUrl = formation.picture
        ? `${API_BASE_URL}/api/formations/images/${formation.picture}`
        : null;

    const objectifs = [formation.objectif, formation.objectif2, formation.objectif3]
        .filter(Boolean)
        .flatMap((v) => String(v).split(/[\n;•]+/).map((p) => p.trim()).filter(Boolean));

    const prerequisList = formation.prerequis
        ? String(formation.prerequis).split(/[\n;]+/).map((p) => p.trim()).filter(Boolean)
        : [];

    return (
        <div className="fm-details-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="fm-details-modal" onMouseDown={(e) => e.stopPropagation()}>

                {/* Banner */}
                <div className="fm-details-banner">
                    {imageUrl ? (
                        <img src={imageUrl} alt={formation.nom} />
                    ) : (
                        <div className="fm-details-placeholder-banner">
                            <div className="fm-details-placeholder-icon"><BookOpen size={28} /></div>
                            <div className="fm-details-placeholder-text">Aucune image</div>
                        </div>
                    )}
                    <div className="fm-details-badge-top">{formation.type || "Formation"}</div>
                    <button className="fm-details-close" onClick={onClose} type="button">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="fm-details-body">
                    <div className="fm-details-ref">{formation.reference}</div>
                    <h2 className="fm-details-title">{formation.nom || formation.name}</h2>
                    <div className="fm-details-underline" />

                    <div className="fm-details-grid">
                        {/* Colonne principale */}
                        <div className="fm-details-main">
                            <section className="fm-details-section">
                                <div className="fm-section-header">
                                    <div className="fm-section-icon"><Target size={18} /></div>
                                    <h3>Objectif(s)</h3>
                                </div>
                                <ul className="fm-details-list">
                                    {objectifs.length > 0 ? objectifs.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    )) : (
                                        <li className="fm-no-bullet fm-muted">Aucun objectif spécifié</li>
                                    )}
                                </ul>
                            </section>

                            <section className="fm-details-section">
                                <div className="fm-section-header">
                                    <div className="fm-section-icon"><ClipboardCheck size={18} /></div>
                                    <h3>Prérequis</h3>
                                </div>
                                <ul className="fm-details-list">
                                    {prerequisList.length > 0 ? prerequisList.map((p, i) => (
                                        <li key={i}>{p}</li>
                                    )) : (
                                        <li className="fm-no-bullet fm-muted">Aucun prérequis spécifique</li>
                                    )}
                                </ul>
                            </section>

                            {formation.moyenPedagogiques && (
                                <section className="fm-details-section">
                                    <div className="fm-section-header">
                                        <div className="fm-section-icon"><BookOpen size={18} /></div>
                                        <h3>Moyens pédagogiques</h3>
                                    </div>
                                    <p className="fm-details-prose">{formation.moyenPedagogiques}</p>
                                </section>
                            )}

                            {formation.description && (
                                <section className="fm-details-section">
                                    <div className="fm-section-header">
                                        <div className="fm-section-icon"><Filter size={18} /></div>
                                        <h3>Description</h3>
                                    </div>
                                    <p className="fm-details-prose">{formation.description}</p>
                                </section>
                            )}
                        </div>

                        {/* Sidebar */}
                        <aside className="fm-details-sidebar">
                            <div className="fm-info-card">
                                <h4>Informations pratiques</h4>

                                <div className="fm-info-item">
                                    <Users size={18} />
                                    <div>
                                        <label>Public visé</label>
                                        <span>{formation.publiqueVise || "Tous les collaborateurs"}</span>
                                    </div>
                                </div>

                                <div className="fm-info-item">
                                    <Clock size={18} />
                                    <div>
                                        <label>Durée</label>
                                        <span>
                                            {formation.duration != null ? `${formation.duration} ${formation.typeDuree || "jour(s)"}` : "—"}
                                        </span>
                                    </div>
                                </div>

                                <div className="fm-info-item">
                                    <BookOpen size={18} />
                                    <div>
                                        <label>Domaine / Thème</label>
                                        <span>{formation.domaineNom || "—"} {formation.themeNom ? `· ${formation.themeNom}` : ""}</span>
                                    </div>
                                </div>

                                {formation.cout != null && (
                                    <div className="fm-info-item">
                                        <Target size={18} />
                                        <div>
                                            <label>Coût</label>
                                            <span>{formation.cout}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                className="fm-details-action-btn"
                                onClick={() => { onClose(); onEdit && onEdit(formation); }}
                            >
                                <Edit2 size={15} style={{ marginRight: 8 }} />
                                Modifier cette formation
                            </button>
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────── */
const FormationManagement = () => {
    const dispatch = useDispatch();
    const { list, loading, bulkLoading, success, importResult } = useSelector((state) => state.formations || {});
    const { domaines, themesFiltered, themes } = useSelector((state) => state.paramDomainesThemes || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [imagePreview, setImagePreview] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkFileName, setBulkFileName] = useState("");
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add");
    const [q, setQ] = useState("");
    const [tab, setTab] = useState("ALL");
    const [page, setPage] = useState(1);
    const [orderedFormations, setOrderedFormations] = useState([]);
    const [draggingId, setDraggingId] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    useEffect(() => {
        dispatch(fetchFormationsRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (success && isModalOpen) {
            closeModal();
            dispatch(fetchFormationsRequest());
        }
    }, [success]);

    useEffect(() => {
        if (!importResult || loading) return;
        if (importResult.dryRun) {
            setBulkOpen(false);
            setIsPreviewOpen(true);
            return;
        }
        setIsPreviewOpen(false);
        setBulkOpen(false);
        setBulkFile(null);
        setBulkFileName("");
    }, [importResult, loading]);

    const formations = useMemo(() => (Array.isArray(list) ? list : []), [list]);

    useEffect(() => {
        if (draggingId != null) return;
        const sorted = [...formations].sort((a, b) => {
            const oa = a?.ordre ?? Number.MAX_SAFE_INTEGER;
            const ob = b?.ordre ?? Number.MAX_SAFE_INTEGER;
            if (oa !== ob) return oa - ob;
            return (a?.id ?? 0) - (b?.id ?? 0);
        });
        setOrderedFormations(sorted);
    }, [formations, draggingId]);

    useEffect(() => { setPage(1); }, [q, tab]);

    const getTypeDisplayLabel = (type) => {
        const labels = {
            "Présentielle": "Présentielle",
            Distancielle: "Distancielle",
            Hybride: "Hybride",
            "E-learning": "E-learning",
        };
        return labels[type] || type || "—";
    };

    const TYPES = ["Présentielle", "Distancielle", "Hybride", "E-learning"];

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return orderedFormations.filter((f) => {
            const okTab = tab === "ALL" || f.type === tab;
            const okSearch = !t || `${f.reference} ${f.nom} ${f.domaineNom} ${f.themeNom}`.toLowerCase().includes(t);
            return okTab && okSearch;
        });
    }, [orderedFormations, q, tab]);

    const PAGE_SIZE = 10;
    const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)), [filtered.length]);
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const paged = useMemo(() => {
        const start = (pageSafe - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, pageSafe]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm });
        setImagePreview(null);
        setIsModalOpen(true);
    };

    const openEdit = (f) => {
        setEditing(f);
        setForm({
            ...emptyForm,
            reference: f.reference || "",
            description: f.description || "",
            duration: f.duration ?? "",
            cout: f.cout ?? "",
            nombreMaxGroupe: f.nombreMaxGroupe ?? "",
            ordre: f.ordre ?? "",
            prerequis: f.prerequis || "",
            objectif: f.objectif || "",
            objectif2: f.objectif2 || "",
            objectif3: f.objectif3 || "",
            typeDuree: f.typeDuree || "Jours",
            moyenPedagogiques: f.moyenPedagogiques || "",
            nom: f.nom || "",
            publiqueVise: f.publiqueVise || "",
            type: f.type || "Présentielle",
            periods: f.periods || "",
            niveaux: f.niveaux || "",
            domaineId: f.domaineId || "",
            themeId: f.themeId || "",
            imageFile: null,
        });
        setImagePreview(f.picture ? `${API_BASE_URL}/api/formations/images/${f.picture}` : null);
        if (f.domaineId) dispatch(fetchThemesByDomaineRequest(f.domaineId));
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        setForm({ ...emptyForm });
        setImagePreview(null);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setForm((p) => ({ ...p, imageFile: file }));
        setImagePreview(URL.createObjectURL(file));
    };

    const onSelectDomaine = (e) => {
        const domaineId = e.target.value;
        setForm((p) => ({ ...p, domaineId, themeId: "" }));
        if (domaineId) dispatch(fetchThemesByDomaineRequest(Number(domaineId)));
    };

    const submit = (e) => {
        if (e) e.preventDefault();
        if (!form.reference?.trim()) { Swal.fire({ icon: "warning", text: "Référence obligatoire.", confirmButtonColor: "#2f4595" }); return; }
        if (!form.ordre || String(form.ordre).trim() === "") { Swal.fire({ icon: "warning", text: "Ordre obligatoire.", confirmButtonColor: "#2f4595" }); return; }
        if (!form.domaineId) { Swal.fire({ icon: "warning", text: "Veuillez choisir un domaine.", confirmButtonColor: "#2f4595" }); return; }

        const payload = {
            ...form,
            reference: form.reference.trim(),
            duration: form.duration === "" ? null : Number(form.duration),
            cout: form.cout === "" ? null : Number(form.cout),
            nombreMaxGroupe: form.nombreMaxGroupe === "" ? null : Number(form.nombreMaxGroupe),
            ordre: Number(form.ordre),
            domaineId: Number(form.domaineId),
            themeId: form.themeId ? Number(form.themeId) : null,
        };

        if (editing?.id) {
            dispatch(updateFormationRequest(editing.id, payload));
        } else {
            dispatch(createFormationRequest(payload));
        }
    };

    const confirmDelete = (f) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer la formation "${f.reference}" ? Cette action est irréversible.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Non, annuler",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#2f4595",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteFormationRequest(f.id));
        });
    };

    const openDetails = (f) => {
        setDetailsFormation(f);
        setDetailsOpen(true);
    };

    /* ── Drag & drop ── */
    const reorderArray = (arr, fromIndex, toIndex) => {
        if (fromIndex === toIndex) return arr;
        const next = [...arr];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
    };

    const buildUpdatePayloadFromFormation = (f, ordre) => ({
        reference: f.reference || "",
        nom: f.nom || "",
        description: f.description || "",
        duration: f.duration === "" || f.duration == null ? null : Number(f.duration),
        cout: f.cout === "" || f.cout == null ? null : Number(f.cout),
        nombreMaxGroupe: f.nombreMaxGroupe === "" || f.nombreMaxGroupe == null ? null : Number(f.nombreMaxGroupe),
        ordre: Number(ordre),
        prerequis: f.prerequis || "",
        objectif: f.objectif || "",
        objectif2: f.objectif2 || "",
        objectif3: f.objectif3 || "",
        typeDuree: f.typeDuree || "Jours",
        moyenPedagogiques: f.moyenPedagogiques || "",
        publiqueVise: f.publiqueVise || "",
        type: f.type || "Présentielle",
        periods: f.periods || "",
        niveaux: f.niveaux || "",
        domaineId: f.domaineId == null || f.domaineId === "" ? null : Number(f.domaineId),
        themeId: f.themeId == null || f.themeId === "" ? null : Number(f.themeId),
        imageFile: null,
        __silent: true,
    });

    const updateFormationOrderDirectly = async (id, payload) => {
        const token = sessionStorage.getItem("token");
        const cleanPayload = { ...payload };
        delete cleanPayload.imageFile;
        delete cleanPayload.__silent;
        const fd = new FormData();
        fd.append("payload", JSON.stringify(cleanPayload));
        if (payload.imageFile) fd.append("image", payload.imageFile);
        return putRequest({ url: `${API_BASE_URL}/api/formations/${id}`, data: fd, token });
    };

    const saveReorder = async (reorderedList, oldList) => {
        const changedItems = reorderedList
            .map((item, index) => {
                const previous = oldList.find((oldItem) => Number(oldItem.id) === Number(item.id));
                const newOrder = index + 1;
                const oldOrder = Number(previous?.ordre);
                if (!previous || oldOrder === newOrder) return null;
                return { formation: previous, newOrder };
            })
            .filter(Boolean);

        if (changedItems.length === 0) return;
        setIsSavingOrder(true);

        try {
            for (let index = 0; index < changedItems.length; index++) {
                const { formation } = changedItems[index];
                await updateFormationOrderDirectly(formation.id, buildUpdatePayloadFromFormation(formation, 100000 + index));
            }
            for (const item of changedItems) {
                await updateFormationOrderDirectly(item.formation.id, buildUpdatePayloadFromFormation(item.formation, item.newOrder));
            }
            await Swal.fire({ icon: "success", title: "Succès", text: "Ordre des formations mis à jour", timer: 2000, showConfirmButton: false });
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'ordre", error);
            setOrderedFormations(oldList);
            await Swal.fire({ icon: "error", title: "Erreur", text: "Erreur lors de la mise à jour de l'ordre" });
        } finally {
            setIsSavingOrder(false);
            setDraggingId(null);
            dispatch(fetchFormationsRequest());
        }
    };

    const buildReorderedFullList = (draggedId, targetId) => {
        const activeList = filtered;
        const fromIndex = activeList.findIndex((item) => Number(item.id) === Number(draggedId));
        const toIndex = activeList.findIndex((item) => Number(item.id) === Number(targetId));
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;

        const reorderedActive = reorderArray(activeList, fromIndex, toIndex);
        const reorderedIds = reorderedActive.map((item) => Number(item.id));
        let activeCursor = 0;

        return orderedFormations.map((item, index) => {
            if (!reorderedIds.includes(Number(item.id))) return { ...item, ordre: index + 1 };
            const nextItem = reorderedActive[activeCursor++];
            return { ...nextItem, ordre: index + 1 };
        });
    };

    const onDragStart = (id) => (e) => {
        if (isSavingOrder) { e.preventDefault(); return; }
        setDraggingId(id);
        try { e.dataTransfer.setData("text/plain", String(id)); } catch (_) {}
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragEnd = () => { setDraggingId(null); };

    const onDragOverRow = (e) => {
        if (isSavingOrder) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDropOnRow = (targetId) => async (e) => {
        e.preventDefault();
        if (isSavingOrder) return;
        const raw = (() => { try { return e.dataTransfer.getData("text/plain"); } catch (_) { return ""; } })();
        const draggedId = draggingId ?? (raw ? Number(raw) : null);
        setDraggingId(null);
        if (!draggedId || !targetId || draggedId === targetId) return;

        const oldList = orderedFormations.map((item) => ({ ...item }));
        const reorderedList = buildReorderedFullList(draggedId, targetId);
        if (!reorderedList) return;

        const hasChanged = reorderedList.some((item, index) => Number(oldList[index]?.id) !== Number(item.id));
        if (!hasChanged) return;

        setOrderedFormations(reorderedList);
        await saveReorder(reorderedList, oldList);
    };

    /* ── Bulk import ── */
    const openBulk = () => {
        setBulkOpen(true);
        setBulkFile(null);
        setBulkFileName("");
        setBulkDryRun(true);
        setBulkAction("add");
        dispatch(clearFormationsImportResult());
    };

    const runBulk = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel.", confirmButtonColor: "#2f4595" }); return; }
        dispatch(importFormationsExcelRequest(bulkFile, { dryRun: bulkDryRun, action: bulkAction }));
    };

    const confirmBulkImport = () => {
        if (!bulkFile) return;
        setIsPreviewOpen(false);
        dispatch(importFormationsExcelRequest(bulkFile, { dryRun: false, action: bulkAction }));
    };

    const bulkSuccessLabel = bulkAction === "edit" ? "Lignes à modifier" : "Lignes à créer";

    const themesToUse = useMemo(() => {
        if (Array.isArray(themesFiltered) && themesFiltered.length) return themesFiltered;
        return Array.isArray(themes) ? themes : [];
    }, [themesFiltered, themes]);

    const typePillClass = (type) => {
        const map = {
            "Présentielle": "fm-pill--blue",
            "Distancielle": "fm-pill--green",
            "Hybride":      "fm-pill--amber",
            "E-learning":   "fm-pill--red",
        };
        return map[type] || "fm-pill--grey";
    };

    /* ─────────────────────────────────────────────
       RENDU
    ───────────────────────────────────────────── */
    return (
        <div className="fm-page">

            {/* ── En-tête (style ParamNF) ── */}
            <div className="fm-header">
                <div className="fm-header-info">
                    <h2>Gestion des formations</h2>
                </div>

            </div>

            {/* ── Card principale avec tabs (style ParamNF) ── */}
            <div className="fm-card">

                {/* Tabs — types de formation */}
                <div className="fm-tabs">
                    {[{ key: "ALL", label: "Toutes" }, ...TYPES.map((t) => ({ key: t, label: getTypeDisplayLabel(t) }))].map((t) => (
                        <button
                            key={t.key}
                            className={`fm-tab ${tab === t.key ? "active" : ""}`}
                            onClick={() => setTab(t.key)}
                            type="button"
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Body */}
                <div className="fm-body">

                    {/* Section top — recherche à gauche, boutons à droite sur la même ligne */}
                    <div className="fm-section-top">
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div className="fm-search-wrap">
                                <Search size={14} className="fm-search-icon" />
                                <input
                                    className="fm-search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Référence, titre, domaine…"
                                />
                            </div>
                            {isSavingOrder && (
                                <span className="fm-saving-badge">Sauvegarde de l'ordre…</span>
                            )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button className="fm-btn fm-btn-light" onClick={() => dispatch(downloadFormationsTemplateRequest())} disabled={loading} type="button">
                                <FileSpreadsheet size={14} /> Template
                            </button>
                            <button className="fm-btn fm-btn-light" onClick={() => dispatch(exportFormationsRequest())} disabled={loading} type="button">
                                <Download size={14} /> Exporter
                            </button>
                            <button className="fm-btn fm-btn-light" onClick={openBulk} type="button">
                                <Upload size={14} /> Import massif
                            </button>
                            <button className="fm-btn fm-btn-primary formation-add-btn" onClick={openCreate} type="button">
                                <Plus size={16} /> Ajouter
                            </button>
                        </div>
                    </div>

                    {/* Tableau (style ParamNF nfTableWrap / nfTable) */}
                    <div className="fm-table-wrap">
                        {loading && <div className="fm-loading">Chargement…</div>}
                        <table className="fm-table">
                            <thead>
                            <tr>
                                <th style={{ width: 34 }}></th>
                                <th>Référence</th>
                                <th>Nom</th>
                                <th>Domaine</th>
                                <th>Thème</th>
                                <th>Type</th>
                                <th>Ordre</th>
                                <th>Image</th>
                                <th className="is-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="fm-empty-cell">
                                        {loading ? "Recherche en cours…" : "Aucun résultat pour cette recherche"}
                                    </td>
                                </tr>
                            ) : paged.map((f) => (
                                <tr
                                    key={f.id}
                                    onDragOver={onDragOverRow}
                                    onDrop={onDropOnRow(f.id)}
                                    style={isSavingOrder ? { opacity: 0.8 } : undefined}
                                >
                                    <td>
                                        <span
                                            className="fm-drag-handle"
                                            draggable={!isSavingOrder}
                                            onDragStart={onDragStart(f.id)}
                                            onDragEnd={onDragEnd}
                                            title="Déplacer"
                                            style={isSavingOrder ? { cursor: "not-allowed", opacity: 0.45, pointerEvents: "none" } : undefined}
                                        >
                                            <GripVertical size={15} />
                                        </span>
                                    </td>
                                    <td>
                                        <span className="fm-mono">{f.reference}</span>
                                    </td>
                                    <td>
                                        <span className="fm-strong" style={{ fontWeight: 600, color: "#111827", fontSize: 12 }}>{f.nom}</span>
                                    </td>
                                    <td>
                                        <span className="fm-badge fm-badge--blue">{f.domaineNom || "—"}</span>
                                    </td>
                                    <td>
                                        <span className="fm-badge fm-badge--grey">{f.themeNom || "—"}</span>
                                    </td>
                                    <td>
                                        <span className={`fm-pill ${typePillClass(f.type)}`}>{getTypeDisplayLabel(f.type)}</span>
                                    </td>
                                    <td>
                                        <span className="fm-badge fm-badge--soft-red">{f.ordre ?? "—"}</span>
                                    </td>
                                    <td>
                                        {f.picture ? (
                                            <img
                                                className="fm-thumb"
                                                src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                alt="img"
                                            />
                                        ) : <span style={{ opacity: 0.3, fontSize: 11 }}>—</span>}
                                    </td>
                                    <td className="is-right">
                                        <div className="fm-btn-group">
                                            <button className="fm-icon-btn" title="Visualiser" onClick={() => openDetails(f)} type="button">
                                                <Eye size={16} />
                                            </button>
                                            <button className="fm-icon-btn" title="Éditer" onClick={() => openEdit(f)} type="button">
                                                <Edit2 size={16} />
                                            </button>
                                            <button className="fm-icon-btn fm-icon-btn--danger" title="Supprimer" onClick={() => confirmDelete(f)} type="button">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="fm-pagination">
                        <button className="fm-btn fm-btn-light" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1} type="button">
                            Précédent
                        </button>
                        <div className="fm-pagination-info">
                            Page <b>{pageSafe}</b> / <b>{totalPages}</b>
                        </div>
                        <button className="fm-btn fm-btn-light" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages} type="button">
                            Suivant
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Modal Détails (INCHANGÉ) ── */}
            <FormationDetailsModal
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                formation={detailsFormation}
                onEdit={openEdit}
            />

            {/* ── Modal CRUD ── */}
            <Modal
                open={isModalOpen}
                title={editing ? "Édition de formation" : "Création de formation"}
                onClose={closeModal}
                footer={
                    <>
                        <button className="fm-btn fm-btn-light" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                        <button className="fm-btn fm-btn-blue" onClick={submit} disabled={loading} type="button">
                            <Save size={14} /> {loading ? "Traitement…" : "Enregistrer"}
                        </button>
                    </>
                }
            >
                {/* Section 1 */}
                <p className="fm-section-title">Informations générales</p>
                <div className="fm-grid">
                    <div className="fm-field">
                        <label>Référence *</label>
                        <input name="reference" value={form.reference} onChange={onChange} placeholder="ex: FOR-2024-01" />
                    </div>
                    <div className="fm-field">
                        <label>Nom</label>
                        <input name="nom" value={form.nom} onChange={onChange} placeholder="Intitulé complet" />
                    </div>
                    <div className="fm-field fm-span-2">
                        <label>Description</label>
                        <textarea name="description" value={form.description} onChange={onChange} rows={3} placeholder="Description de la formation…" />
                    </div>
                    <div className="fm-field fm-span-2">
                        <label>Image</label>
                        <input type="file" accept="image/*" onChange={onPickImage} />
                        {imagePreview && (
                            <div className="fm-preview">
                                <img src={imagePreview} alt="Preview" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 2 */}
                <p className="fm-section-title">Classification</p>
                <div className="fm-grid">
                    <div className="fm-field">
                        <label>Domaine *</label>
                        <select name="domaineId" value={form.domaineId} onChange={onSelectDomaine}>
                            <option value="">-- Choisir --</option>
                            {(domaines || []).map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                        </select>
                    </div>
                    <div className="fm-field">
                        <label>Thème</label>
                        <select name="themeId" value={form.themeId} onChange={onChange}>
                            <option value="">-- Choisir --</option>
                            {themesToUse.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
                        </select>
                    </div>
                    <div className="fm-field">
                        <label>Type</label>
                        <select name="type" value={form.type} onChange={onChange}>
                            <option value="Présentielle">Présentielle</option>
                            <option value="Distancielle">Distancielle</option>
                            <option value="Hybride">Hybride</option>
                            <option value="E-learning">E-learning</option>
                        </select>
                    </div>
                    <div className="fm-field">
                        <label>Niveau</label>
                        <input name="niveaux" value={form.niveaux} onChange={onChange} placeholder="ex: Tous niveaux, Expert…" />
                    </div>
                </div>

                {/* Section 3 */}
                <p className="fm-section-title">Organisation</p>
                <div className="fm-grid">
                    <div className="fm-field">
                        <label>Durée</label>
                        <input name="duration" value={form.duration} onChange={onChange} type="number" step="0.5" />
                    </div>
                    <div className="fm-field">
                        <label>Type de durée</label>
                        <select name="typeDuree" value={form.typeDuree} onChange={onChange}>
                            <option value="Jours">Jours</option>
                            <option value="Heures">Heures</option>
                        </select>
                    </div>
                    <div className="fm-field">
                        <label>Coût</label>
                        <input name="cout" value={form.cout} onChange={onChange} type="number" />
                    </div>
                    <div className="fm-field">
                        <label>Nombre Max de groupe</label>
                        <input name="nombreMaxGroupe" value={form.nombreMaxGroupe} onChange={onChange} type="number" />
                    </div>
                    <div className="fm-field">
                        <label>Ordre *</label>
                        <input name="ordre" value={form.ordre} onChange={onChange} type="number" />
                    </div>
                </div>

                {/* Section 4 */}
                <p className="fm-section-title">Contenu pédagogique</p>
                <div className="fm-grid">
                    <div className="fm-field fm-span-2">
                        <label>Prérequis</label>
                        <textarea name="prerequis" value={form.prerequis} onChange={onChange} rows={2} />
                    </div>
                    <div className="fm-field fm-span-2">
                        <label>Public visé</label>
                        <textarea name="publiqueVise" value={form.publiqueVise} onChange={onChange} rows={2} />
                    </div>
                    <div className="fm-field fm-span-2">
                        <label>Moyens pédagogiques</label>
                        <textarea name="moyenPedagogiques" value={form.moyenPedagogiques} onChange={onChange} rows={2} />
                    </div>
                    <div className="fm-field">
                        <label>Objectif 1</label>
                        <textarea name="objectif" value={form.objectif} onChange={onChange} rows={2} />
                    </div>
                    <div className="fm-field">
                        <label>Objectif 2</label>
                        <textarea name="objectif2" value={form.objectif2} onChange={onChange} rows={2} />
                    </div>
                    <div className="fm-field">
                        <label>Objectif 3</label>
                        <textarea name="objectif3" value={form.objectif3} onChange={onChange} rows={2} />
                    </div>
                </div>
            </Modal>

            {/* ── Modal Import massif ── */}
            {bulkOpen && (
                <div className="fm-modal-overlay" onClick={() => setBulkOpen(false)}>
                    <div className="fm-modal fm-modal--medium" onClick={(e) => e.stopPropagation()}>
                        <div className="fm-modal-header">
                            <h3><Upload size={16} /> Import massif</h3>
                            <button className="fm-x" onClick={() => setBulkOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="fm-modal-body">
                            <div className="fm-box">
                                <p className="fm-section-title">Fichier Excel</p>
                                <div className="fm-import-row">
                                    <label className={`fm-file-label ${bulkFileName ? "has-file" : ""}`}>
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                const f = e.target.files?.[0] || null;
                                                setBulkFile(f);
                                                setBulkFileName(f ? f.name : "");
                                            }}
                                        />
                                        <Upload size={14} />
                                        <span>{bulkFileName || "Choisir un fichier (.xlsx, .xls)"}</span>
                                    </label>
                                    <select style={{ width: 140, height: 34, fontSize: 12 }} value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                        <option value="add">Ajout</option>
                                        <option value="edit">Mise à jour</option>
                                    </select>
                                </div>
                                <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, fontSize: 12, cursor: "pointer" }}>
                                    <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} style={{ width: 16, height: 16 }} />
                                    Mode simulation (vérifier sans enregistrer)
                                </label>
                                <p style={{ marginTop: 12, fontSize: 12, color: "#475569" }}>
                                    Pour Domaine et Thème, veuillez sélectionner une valeur depuis la liste déroulante du template.
                                </p>
                            </div>

                            {importResult && (
                                <div className="fm-import-result">
                                    <p className="fm-section-title">Compte-rendu d'intégration</p>
                                    <div className="fm-import-summary">
                                        <div className="fm-summary-item">Lignes analysées : <b>{importResult.totalRows}</b></div>
                                        <div className="fm-summary-item">{bulkSuccessLabel} : <b style={{ color: "#10b981" }}>{importResult.createdCount}</b></div>
                                        <div className="fm-summary-item">Ignorées : <b>{importResult.skippedCount}</b></div>
                                        <div className="fm-summary-item">Erreurs : <b style={{ color: "#dc3545" }}>{(importResult.errors || []).length}</b></div>
                                    </div>
                                    {(importResult.errors || []).length > 0 && (
                                        <div className="fm-table-wrap" style={{ maxHeight: 180, border: "1px solid #fee2e2", borderRadius: 8 }}>
                                            <table className="fm-table" style={{ fontSize: 11 }}>
                                                <thead>
                                                <tr>
                                                    <th style={{ background: "#fef2f2", color: "#991b1b" }}>Ligne</th>
                                                    <th style={{ background: "#fef2f2", color: "#991b1b" }}>Colonne</th>
                                                    <th style={{ background: "#fef2f2", color: "#991b1b" }}>Message</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {importResult.errors.map((er, idx) => (
                                                    <tr key={idx}>
                                                        <td>{er.row}</td>
                                                        <td style={{ fontWeight: 700 }}>{er.column}</td>
                                                        <td style={{ color: "#991b1b" }}>{er.message}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="fm-modal-footer">
                            <button className="fm-btn fm-btn-light" onClick={() => dispatch(downloadFormationsTemplateRequest())} type="button">
                                <FileSpreadsheet size={14} /> Template
                            </button>
                            <button className="fm-btn fm-btn-light" onClick={() => setBulkOpen(false)} type="button">Annuler</button>
                            <button className="fm-btn fm-btn-blue" onClick={runBulk} disabled={bulkLoading || !bulkFile} type="button">
                                <Upload size={14} /> {bulkLoading ? "Traitement…" : (bulkDryRun ? "Simuler" : "Importer")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Prévisualisation dry-run ── */}
            {isPreviewOpen && importResult && (
                <div className="fm-modal-overlay" onClick={() => setIsPreviewOpen(false)}>
                    <div className="fm-modal fm-modal--large" onClick={(e) => e.stopPropagation()}>
                        <div className="fm-modal-header">
                            <h3><CheckCircle size={16} /> Résultat de vérification</h3>
                            <button className="fm-x" onClick={() => setIsPreviewOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="fm-modal-body">
                            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                                <div style={{ background: "#d1fae5", padding: "8px 12px", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <CheckCircle size={14} style={{ color: "#059669" }} />
                                    <span style={{ fontSize: 12, color: "#065f46" }}>{importResult.createdCount} Prêtes</span>
                                </div>
                                <div style={{ background: "#fde8e7", padding: "8px 12px", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <AlertTriangle size={14} style={{ color: "#dc3545" }} />
                                    <span style={{ fontSize: 12, color: "#9b1c1c" }}>{importResult.skippedCount} Erreurs</span>
                                </div>
                            </div>
                            <div style={{ marginBottom: 16, fontSize: 12, color: "#475569" }}>
                                Lignes analysées : <b>{importResult.totalRows}</b> | {bulkSuccessLabel} : <b>{importResult.createdCount}</b> | Ignorées : <b>{importResult.skippedCount}</b>
                            </div>
                            {(importResult.errors || []).length > 0 && (
                                <div className="fm-box" style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
                                    <p className="fm-section-title" style={{ color: "#9b1c1c" }}>Détails des erreurs</p>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {importResult.errors.map((err, i) => (
                                            <div key={i} style={{ fontSize: 11, color: "#9b1c1c" }}>
                                                Ligne {err.row} : <b>{err.column}</b> — {err.message}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="fm-modal-footer">
                            <button className="fm-btn fm-btn-light" onClick={() => setIsPreviewOpen(false)} type="button">Annuler</button>
                            <button
                                className="fm-btn fm-btn-blue"
                                onClick={confirmBulkImport}
                                disabled={loading || importResult.createdCount === 0}
                                type="button"
                            >
                                Confirmer l'import ({importResult.createdCount})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormationManagement;
